'use client';

import type { PropsWithChildren } from 'react';
import React, { useCallback, useMemo, useReducer, useRef } from 'react';
import { Portal } from 'components/Portal';
import { ToastContainer } from './ToastContainer';
import { ToastContext } from './ToastContext';
import { ToastItem } from './ToastItem';
import {
  type ToastContextType,
  type ToastItemInstance,
  type ToastMessage,
  type ToastOptions,
  type ToastPlacement,
} from './ToastTypes';

type State = Record<ToastPlacement, string[]> & {
  toasts: Record<string, ToastItemInstance>;
  placements: Record<string, ToastPlacement>;
};

type Action =
  | { type: 'ADD'; payload: { toast: ToastItemInstance } }
  | { type: 'REMOVE'; payload: { id: string } }
  | { type: 'HEIGHT'; payload: { id: string; height: number } }
  | {
      type: 'UPDATE';
      payload: {
        id: string;
        message: ToastMessage;
        duration?: ToastOptions['duration'];
      };
    }
  | { type: 'START_EXIT'; payload: { id: string } };

/**
 * Toast state handler
 */
const PLACEMENT_OPTIONS: ToastPlacement[] = [
  'top',
  'bottom',
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
];

const DEFAULT_STATE = {
  toasts: {},
  placements: {},
  ...PLACEMENT_OPTIONS.reduce((accumulator, option) => {
    accumulator[option] = [];
    return accumulator;
  }, {} as Record<ToastPlacement, string[]>),
} as State;

function handleAddToast(state: State, toast: ToastItemInstance): State {
  const placement = toast.placement || 'top';

  if (state.toasts[toast.id]) {
    const existingToast = state.toasts[toast.id];
    return {
      ...state,
      toasts: {
        ...state.toasts,
        [toast.id]: {
          ...existingToast,
          duration: toast.duration || existingToast.duration,
          iteration: existingToast.iteration + 1,
        },
      },
    };
  }

  return {
    ...state,
    placements: {
      ...state.placements,
      [toast.id]: placement,
    },
    toasts: {
      ...state.toasts,
      [toast.id]: toast,
    },
    [placement]: [...state[placement], toast.id],
  };
}

function reducer(state: State = DEFAULT_STATE, action: Action) {
  switch (action.type) {
    case 'HEIGHT': {
      const toast = state.toasts[action.payload.id];
      return {
        ...state,
        toasts: {
          ...state.toasts,
          [action.payload.id]: {
            ...toast,
            height: action.payload.height,
          },
        },
      };
    }

    case 'START_EXIT': {
      return {
        ...state,
        toasts: {
          ...state.toasts,
          [action.payload.id]: {
            ...state.toasts[action.payload.id],
            isExiting: true,
          },
        },
      };
    }

    case 'UPDATE': {
      if (!state.toasts[action.payload.id]) {
        // eslint-disable-next-line no-console
        console.error('Cannot update toast that has been removed from state');
        return state;
      }

      return {
        ...state,
        toasts: {
          ...state.toasts,
          [action.payload.id]: {
            ...state.toasts[action.payload.id],
            duration: action.payload.duration,
            message: action.payload.message,
          },
        },
      };
    }

    case 'ADD':
      return handleAddToast(state, action.payload.toast);

    case 'REMOVE': {
      const placement = state.placements[action.payload.id];
      const toasts = state[placement];
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [action.payload.id]: _, ...newToasts } = state.toasts;
      return {
        ...state,
        toasts: newToasts,
        [placement]: toasts.filter((toast) => toast !== action.payload.id),
      };
    }
    default:
      return state;
  }
}

/**
 * Provide a place in the DOM to launch our toasts. This should typically
 * be at the uppermost part of your app tree.
 */

export type ToastProviderProps = PropsWithChildren<{
  /** the space between toasts and the screen */
  margin?: string;
  /**
   * the default close label for toasts
   * @note this can be overridden by the message prop
   * @default 'Dismiss'
   */
  defaultCloseLabel?: string;
}>;

export function ToastProvider({
  children,
  margin = '0px',
  defaultCloseLabel = 'Dismiss', // using an inline string to help define it in one instance but allow it to be overridden by the consumer
}: ToastProviderProps) {
  const idCounterRef = useRef(0);
  const [statePlacements, dispatch] = useReducer(reducer, DEFAULT_STATE);

  const addToast = useCallback(
    (message: ToastMessage, options?: ToastOptions): string => {
      const id = options?.id ?? String(idCounterRef.current);
      idCounterRef.current += 1;
      const newToast: ToastItemInstance = {
        id,
        iteration: 0,
        message,
        ...options,
      };
      dispatch({ type: 'ADD', payload: { toast: newToast } });
      return newToast.id;
    },
    []
  );

  const updateToast = useCallback(
    (
      id: string,
      message: ToastMessage,
      options?: Pick<ToastOptions, 'duration'>
    ): void => {
      dispatch({
        type: 'UPDATE',
        payload: {
          id,
          message,
          ...options,
        },
      });
    },
    []
  );

  const removeToast: ToastContextType['removeToast'] = useCallback(
    (id: string): void => {
      dispatch({ type: 'START_EXIT', payload: { id } });
    },
    []
  );

  // register the toast context with the so we can use it throughout the app
  const toastContext: ToastContextType = useMemo(
    () => ({
      addToast,
      removeToast,
      updateToast,
      defaultCloseLabel,
    }),
    [addToast, removeToast, updateToast, defaultCloseLabel]
  );

  return (
    <ToastContext.Provider value={toastContext}>
      <Portal hasOutermostTheme={true}>
        <>
          {PLACEMENT_OPTIONS.map((placement) => {
            // get the toasts in the current placement and render them in their respective container
            const toastsInPlace = statePlacements[placement];
            let precedingHeight = 0;
            return (
              <ToastContainer
                key={placement}
                $margin={margin}
                aria-live="polite"
                data-placement={placement}
              >
                {toastsInPlace.map((toastId) => {
                  const toast = statePlacements.toasts[toastId];
                  const toastHeight = toast.isExiting ? 0 : toast.height ?? 0;
                  precedingHeight += toastHeight;

                  const registerHeight = (height: number) =>
                    dispatch({
                      type: 'HEIGHT',
                      payload: { id: toast.id, height },
                    });

                  const handleRemoveAnimationComplete = () =>
                    dispatch({ type: 'REMOVE', payload: { id: toast.id } });

                  return (
                    <ToastItem
                      key={toast.id}
                      defaultCloseLabel={defaultCloseLabel}
                      isExiting={toast.isExiting}
                      onRemoveAnimationComplete={handleRemoveAnimationComplete}
                      placement={placement}
                      precedingHeight={precedingHeight - toastHeight}
                      registerHeight={registerHeight}
                      removeToast={removeToast}
                      toast={toast}
                    />
                  );
                })}
              </ToastContainer>
            );
          })}
        </>
      </Portal>
      {children}
    </ToastContext.Provider>
  );
}
